import React, { useState, useEffect, useRef } from "react"
import DropdownButtonLookook from "@components/common/DropdownButtonLookook"
import CheckBox from "@components/common/CheckBox"
import { GatsbyImage } from "gatsby-plugin-image"
import { getShopifyImage } from "gatsby-source-shopify"

const LookBookItem = ({
	data,
	selectAll,
	productSelected,
	setProductSelected,
	scrollRef = null,
}) => {
	const [isChecked, setIsChecked] = useState(false)
	const [sizes, setSizes] = useState("")
	const [size, setSize] = useState("")
	const [price, setPrice] = useState("")
	const isFirstRender = useRef(true)
	const itemRef = useRef()

	const previousSizeRef = useRef()

	useEffect(() => {
		const previousSize = previousSizeRef.current

		// If size changed and previousSize is in productSelected, remove it
		if (previousSize && previousSize !== size) {
			setProductSelected((prev) => prev.filter((item) => item !== previousSize))
		}

		// Store current size for next change
		previousSizeRef.current = size
	}, [size])

	const handleSizeChange = (item) => {
		setSize(item)
	}

	const handleCheckBox = () => {
		const updatedChecked = !isChecked

		//size
		setIsChecked(updatedChecked)

		if (updatedChecked) {
			// Add size if not already in productSelected
			if (!productSelected.includes(size)) {
				setProductSelected([...productSelected, size])
			}
		} else {
			// Remove size from productSelected
			setProductSelected(productSelected.filter((item) => item !== size))
		}
	}

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false
			return // skip on initial mount
		}
		if (!size) return // Skip if size is not selected yet

		if (selectAll) {
			setProductSelected((prev) => {
				if (!prev.includes(size)) {
					return [...prev, size]
				}
				return prev
			})
			setIsChecked(true)
		} else {
			setProductSelected((prev) => prev.filter((item) => item !== size))
			setIsChecked(false)
		}
	}, [selectAll, size])

	useEffect(() => {
		const fetchInventory = async () => {
			const productUrl = data?.productUrl
			const handle = productUrl.split("/products/")[1]

			const response = await fetch(
				"https://" +
					process.env.GATSBY_MYSHOPIFY_URL +
					"/api/2025-01/graphql.json",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Shopify-Storefront-Access-Token":
							process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
					},
					body: JSON.stringify({
						query: `
                query GetProductInventory($handle: String!) {
                    productByHandle(handle: $handle) {
                        variants(first: 50) {
                            edges {
                                node {
                                    title
                                    availableForSale
                                    quantityAvailable
                                    id
                                    price {
                                        amount
                                        currencyCode
                                    }
                                }
                            }
                        }
                    }
                }
                `,
						variables: { handle: handle },
					}),
				}
			)

			const result = await response.json()
			const sizes =
				result?.data?.productByHandle?.variants?.edges?.map(
					(variant) => variant?.node
				) || []

			const filteredSizes = sizes.filter((v) => v.availableForSale)

			setSizes(filteredSizes)
		}

		fetchInventory()
	}, [data])

	useEffect(() => {
		setPrice(size?.price?.amount)
	}, [size])

	const variantImage = React.useMemo(
		() => ({
			originalSrc: data?.imageUrl,
		}),
		[data?.imageUrl]
	)

	const image = React.useMemo(
		() =>
			getShopifyImage({
				image: variantImage,
				layout: "constrained",
				crop: "contain",
				width: 160,
				height: 240,
			}),
		[variantImage]
	)

	return (
		<React.Fragment>
			<div className={`flex flex-nowrap gap-x-4`} ref={itemRef}>
				<CheckBox
					onChange={() => handleCheckBox()}
					checked={isChecked}
					disabled={sizes?.length > 0 ? false : true}
				/>
				{image && (
					<GatsbyImage
						key={data?.productId}
						image={image}
						className="w-20 h-30 object-cover"
						alt={"lookbook-thumb"}
					/>
				)}
				<div className="flex flex-col justify-between flex-1">
					<div>
						<h6 className="text-md">{data?.title?.split(" / ")?.[0]}</h6>
						<div className="flex gap-1">{data?.title?.split(" / ")?.[1]}</div>
					</div>
					<div className="flex w-full items-center justify-between relative">
						<div className="relative flex flex-nowrap gap-1 text-xs items-center">
							{sizes?.length > 0 ? (
								<>
									<span className="text-slate-300 mr-2">SIZE:</span>
									<DropdownButtonLookook
										itemRef={itemRef}
										className="text-gray-500 text-xs"
										caretSize={16}
										defaultOption={sizes[0]}
										optionListWidth={"min-w-20"}
										options={sizes}
										onClick={(e) => handleSizeChange(e)}
										useLabelName="displayValue"
										scrollRef={scrollRef}
										noShadows
									/>
								</>
							) : (
								<span className="mr-2 text-red-600 uppercase">
									Out of Stock
								</span>
							)}
						</div>
						{sizes?.length > 0 ? (
							<div>
								{process.env.GATSBY_STORE !== "PH" ? "$" : "₱"}
								{price}
							</div>
						) : (
							<div> </div>
						)}
					</div>
				</div>
			</div>
		</React.Fragment>
	)
}

export default LookBookItem
